import { useState } from 'react';

import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { EmptyState, ErrorState, LoadingState, PageContainer } from '@/shared/ui';

import { useInitializeLoyaltyDefaults, useLoyaltyRules, useLoyaltyTiers } from '../../hooks';
import { formatPoints } from '../../lib/format';
import { useLoyaltyPermissions } from '../../lib/useLoyaltyPermissions';
import type { LoyaltyRule, LoyaltyTier } from '../../model/types';
import { EnabledChip, RuleTypeChip } from '../components/chips';
import { CodeText, SectionHeader } from '../components/primitives';
import { ProgramModelCard } from '../components/ProgramModelCard';
import { RuleFormDialog } from '../components/RuleFormDialog';
import { TierFormDialog } from '../components/TierFormDialog';

function paramsSummary(params?: Record<string, unknown>): string {
  if (!params) return '—';
  const entries = Object.entries(params);
  if (entries.length === 0) return '—';
  return entries
    .map(([key, value]) => `${key}=${typeof value === 'object' ? JSON.stringify(value) : String(value)}`)
    .join(', ');
}

/** Loyalty programme administration — model, tier ladder + rules (gated by `loyalty.view`). */
export function LoyaltyProgramPage() {
  const { canView, canManage } = useLoyaltyPermissions();
  const tiers = useLoyaltyTiers({ enabled: canView });
  const rules = useLoyaltyRules({ enabled: canView });
  const initialize = useInitializeLoyaltyDefaults();

  const [tierDialog, setTierDialog] = useState<{ open: boolean; tier: LoyaltyTier | null }>({
    open: false,
    tier: null,
  });
  const [ruleDialog, setRuleDialog] = useState<{ open: boolean; rule: LoyaltyRule | null }>({
    open: false,
    rule: null,
  });
  const [toast, setToast] = useState<string | null>(null);

  if (!canView) {
    return (
      <PageContainer title="Программа лояльности">
        <ErrorState
          title="Access restricted"
          message="У вас нет доступа к программе лояльности."
        />
      </PageContainer>
    );
  }

  const isLoading = tiers.isLoading || rules.isLoading;
  const isError = tiers.isError || rules.isError;
  const tierList = tiers.data ?? [];
  const ruleList = rules.data ?? [];
  const isEmpty = tierList.length === 0 && ruleList.length === 0;

  const seedDefaults = () =>
    initialize.mutate(undefined, {
      onSuccess: () => setToast('Программа по умолчанию создана.'),
      onError: (err) => setToast(err.message),
    });

  return (
    <PageContainer title="Программа лояльности" subtitle="Модель, уровни и правила начисления баллов">
      {!canManage ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          Просмотр без права изменения. Для настройки программы нужно разрешение «loyalty.manage».
        </Alert>
      ) : null}

      <Stack spacing={3}>
        <ProgramModelCard canView={canView} canManage={canManage} onToast={(m) => setToast(m)} />

        {isLoading ? (
          <LoadingState label="Загрузка программы…" />
        ) : isError ? (
          <ErrorState
            title="Не удалось загрузить программу"
            message={tiers.error?.message ?? rules.error?.message ?? 'Произошла ошибка.'}
            onRetry={() => {
              void tiers.refetch();
              void rules.refetch();
            }}
          />
        ) : isEmpty ? (
          <EmptyState
            title="Уровни и правила не настроены"
            description="Создайте базовую лестницу уровней и правило начисления, затем настройте их под себя."
            action={
              canManage ? (
                <Button
                  variant="contained"
                  startIcon={
                    initialize.isPending ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <AutoAwesomeRoundedIcon />
                    )
                  }
                  disabled={initialize.isPending}
                  onClick={seedDefaults}
                >
                  Создать программу по умолчанию
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            {/* Tiers */}
            <Paper variant="outlined">
              <SectionHeader
                title="Уровни"
                onAdd={canManage ? () => setTierDialog({ open: true, tier: null }) : undefined}
                addLabel="Добавить уровень"
              />
              {tierList.length === 0 ? (
                <Box sx={{ p: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Уровней пока нет.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Код</TableCell>
                        <TableCell>Название</TableCell>
                        <TableCell align="right">Минимум баллов</TableCell>
                        <TableCell>Преимущества</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tierList.map((tier) => (
                        <TableRow key={tier.id} hover>
                          <TableCell>
                            <CodeText>{tier.code}</CodeText>
                          </TableCell>
                          <TableCell>{tier.name}</TableCell>
                          <TableCell align="right">{formatPoints(tier.minPoints)}</TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ maxWidth: 320 }}
                              noWrap
                            >
                              {tier.benefits ?? '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {canManage ? (
                              <Tooltip title="Изменить">
                                <IconButton
                                  size="small"
                                  aria-label={`Изменить уровень ${tier.code}`}
                                  onClick={() => setTierDialog({ open: true, tier })}
                                >
                                  <EditRoundedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>

            {/* Rules */}
            <Paper variant="outlined">
              <SectionHeader
                title="Правила"
                onAdd={canManage ? () => setRuleDialog({ open: true, rule: null }) : undefined}
                addLabel="Добавить правило"
              />
              {ruleList.length === 0 ? (
                <Box sx={{ p: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    Правил пока нет.
                  </Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Код</TableCell>
                        <TableCell>Название</TableCell>
                        <TableCell>Тип</TableCell>
                        <TableCell>Статус</TableCell>
                        <TableCell>Параметры</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ruleList.map((rule) => (
                        <TableRow key={rule.id} hover>
                          <TableCell>
                            <CodeText>{rule.code}</CodeText>
                          </TableCell>
                          <TableCell>{rule.name}</TableCell>
                          <TableCell>
                            <RuleTypeChip type={rule.type} />
                          </TableCell>
                          <TableCell>
                            <EnabledChip enabled={rule.enabled} />
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ maxWidth: 320 }}
                              noWrap
                            >
                              {paramsSummary(rule.params)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {canManage ? (
                              <Tooltip title="Изменить">
                                <IconButton
                                  size="small"
                                  aria-label={`Изменить правило ${rule.code}`}
                                  onClick={() => setRuleDialog({ open: true, rule })}
                                >
                                  <EditRoundedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </>
        )}
      </Stack>

      <TierFormDialog
        open={tierDialog.open}
        tier={tierDialog.tier}
        onClose={() => setTierDialog({ open: false, tier: null })}
        onDone={(message) => setToast(message)}
      />
      <RuleFormDialog
        open={ruleDialog.open}
        rule={ruleDialog.rule}
        onClose={() => setRuleDialog({ open: false, rule: null })}
        onDone={(message) => setToast(message)}
      />

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast(null)}
          severity="info"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {toast ?? ''}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
}
